/** @jsxImportSource @emotion/react */

import * as React from 'react'
import { ModalPortal } from './modalPortal'
import { jsx } from '@emotion/react'
import * as classNames from 'classnames'
import { ModalClass } from '@sphinx/components-styles'

interface ModalProps extends React.HtmlHTMLAttributes<HTMLDivElement> {
  closeOnClickAway?: boolean
  closeOnEscape?: boolean
  open?: boolean
  onClose?(): void
  wrapperClassName?: string
  addModalOpenAttributesToDoc?: boolean
  withScroll?: boolean
}

const removeModalAttributes = () => {
  document.body.removeAttribute('modal__body--open')
  document.documentElement.removeAttribute('modal__html--open')
}
const addModalAttributes = () => {
  document.body.setAttribute('modal__body--open', '')
  document.documentElement.setAttribute('modal__html--open', '')
}

export const Modal: React.FC<ModalProps> = ({
  closeOnClickAway = false,
  closeOnEscape = false,
  open = false,
  onClose = () => {},
  className,
  wrapperClassName,
  addModalOpenAttributesToDoc = true,
  withScroll = false,
  ...props
}) => {
  const contentWrapperRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    if (!closeOnEscape) {
      return
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.keyCode === 27) {
        onClose()
      }
    }

    document.addEventListener('keydown', onKeyDown)

    return () => {
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [closeOnEscape])

  React.useEffect(() => {
    if (open && addModalOpenAttributesToDoc) {
      addModalAttributes()
    }
    return removeModalAttributes
  }, [open, addModalOpenAttributesToDoc])

  const handleClickAway = ({ target }: React.MouseEvent<HTMLDivElement>) => {
    if (closeOnClickAway && target == contentWrapperRef.current) {
      onClose()
    }
  }

  const ModalContent = (
    <div
      {...props}
      className={classNames(
        withScroll
          ? ModalClass.modalContentWithScroll
          : ModalClass.modalContent,
        className
      )}
    />
  )

  const ModalContentWrapper = (() => {
    if (withScroll) {
      return (
        <div
          className={classNames(ModalClass.modalContentWithScrollWrapper)}
          ref={contentWrapperRef}
          onClick={handleClickAway}
        >
          {ModalContent}
        </div>
      )
    }
    return ModalContent
  })()

  return (
    open && (
      <ModalPortal>
        <div
          className={classNames(ModalClass.wrap, wrapperClassName)}
          {...props}
        >
          {closeOnClickAway && !withScroll && (
            <div className={ModalClass.clickAwayContainer} onClick={onClose} />
          )}
          {ModalContentWrapper}
        </div>
      </ModalPortal>
    )
  )
}
